<template>
    <div>
        <!-- Generate API Token -->
        <jet-form-section @submitted="createApiToken">
            <template #title>
                Personal API Tokens
            </template>

            <template #description>
                API tokens allow third-party services to authenticate with our application on your behalf - <docs-link
                    href="https://docs.birdie.so/birdie-docs/birdie-api" />
            </template>

            <template #form v-if="has_plan_api">

                <div v-if="tokens.length > 0" class="col-span-6">
                    <!-- API Token List -->
                    <div class="space-y-6 text-sm">
                        <div class="flex items-center justify-between" v-for="token in tokens" :key="token.id">
                            <div>
                                {{ token.name }}
                            </div>

                            <div class="flex items-center">
                                <div class="text-sm text-gray-400" v-if="token.last_used_ago">
                                    Last used {{ token.last_used_ago }}
                                </div>



                                <button class="cursor-pointer ml-6 text-sm text-red-500"
                                    @click.prevent="confirmApiTokenDeletion(token)">
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-span-6" v-if="tokens.length > 0">
                    <jet-section-border />
                </div>

                <!-- Token Name -->

                <div class="col-span-6 flex items-center justify-between gap-x-4 pb-5">
                    <div class="flex items-center flex-grow gap-x-4">
                        <label class="text-sm font-medium text-gray-900" for="name">Create a new Token:</label>
                        <div class="flex-grow flex-col">
                            <jet-input id="name" type="text" class="block w-full max-w-sm shadow-none"
                                placeholder="Token name" v-model="createApiTokenForm.name" autofocus />
                            <jet-input-error :message="createApiTokenForm.errors.name" class="mt-2" />
                        </div>
                    </div>
                    <div class="flex items-center gap-x-4">

                        <jet-action-message :on="createApiTokenForm.recentlySuccessful" class="mr-3">
                            Created.
                        </jet-action-message>

                        <jet-button :class="{ 'opacity-25': createApiTokenForm.processing }"
                            :disabled="createApiTokenForm.processing">
                            Create
                        </jet-button>
                    </div>
                </div>

            </template>

            <template #form v-else>
                <div class="col-span-6 text-sm text-gray-500">You need to upgrade your plan to use
                    API tokens &nbsp; &nbsp;
                    <UpgradePlan />
                </div>
            </template>

        </jet-form-section>


        <!-- Token Value Modal -->
        <jet-dialog-modal :show="displayingToken" @close="displayingToken = false">
            <template #title>
                <span class="text-sm font-medium">API Token</span>
            </template>

            <template #content>
                <div class="text-sm">
                    Please copy your new API token. For security, it won't be shown again.
                </div>

                <div class="mt-4 bg-gray-100 px-4 py-2 rounded font-mono text-sm text-gray-500 relative"
                    v-if="$page.props.jetstream.flash.token">
                    {{ $page.props.jetstream.flash.token }}

                    <a href="#" v-clipboard:copy="$page.props.jetstream.flash.token" @click.prevent.stop="copiedToken"
                        @mouseleave="copied = false"
                        class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center gap-x-1 group">
                        <span v-if="!copied"
                            class="text-xs opacity-0 text-gray-600 group-hover:opacity-100 transition-opacity">copy</span>
                        <span v-else
                            class="text-xs opacity-100 text-gray-600 group-hover:opacity-100 transition-opacity">copied!</span>
                        <ClipboardIcon class="size-4 text-gray-400 group-hover:text-gray-500" />
                    </a>

                </div>

            </template>

            <template #footer>
                <jet-secondary-button @click="displayingToken = false">
                    Close
                </jet-secondary-button>
            </template>
        </jet-dialog-modal>



        <!-- Delete Token Confirmation Modal -->
        <jet-confirmation-modal :show="apiTokenBeingDeleted" @close="apiTokenBeingDeleted = null">
            <template #title>
                Delete API Token
            </template>

            <template #content>
                Are you sure you would like to delete this API token?
            </template>

            <template #footer>
                <jet-secondary-button @click="apiTokenBeingDeleted = null">
                    Cancel
                </jet-secondary-button>

                <jet-danger-button class="ml-2" @click="deleteApiToken"
                    :class="{ 'opacity-25': deleteApiTokenForm.processing }" :disabled="deleteApiTokenForm.processing">
                    Delete
                </jet-danger-button>
            </template>
        </jet-confirmation-modal>
    </div>
</template>

<script>
    import JetActionMessage from '@/Jetstream/ActionMessage'
    import JetActionSection from '@/Jetstream/ActionSection'
    import JetButton from '@/Jetstream/Button'
    import JetConfirmationModal from '@/Jetstream/ConfirmationModal'
    import JetDangerButton from '@/Jetstream/DangerButton'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import JetFormSection from '@/Jetstream/FormSection'
    import JetInput from '@/Jetstream/Input'
    import JetCheckbox from '@/Jetstream/Checkbox'
    import JetInputError from '@/Jetstream/InputError'
    import JetLabel from '@/Jetstream/Label'
    import JetSecondaryButton from '@/Jetstream/SecondaryButton'
    import JetSectionBorder from '@/Jetstream/SectionBorder'
    import { ClipboardIcon } from "@heroicons/vue/24/solid"

    import UpgradePlan from '@/Components/UI/UpgradePlan.vue'

    export default {
        components: {
            JetActionMessage,
            JetActionSection,
            JetButton,
            JetConfirmationModal,
            JetDangerButton,
            JetDialogModal,
            JetFormSection,
            JetInput,
            JetCheckbox,
            JetInputError,
            JetLabel,
            JetSecondaryButton,
            JetSectionBorder, ClipboardIcon, UpgradePlan
        },

        props: [
            'tokens',
            'defaultPermissions',
            'has_plan_api'
        ],

        data() {
            return {
                createApiTokenForm: this.$inertia.form({
                    name: '',
                    permissions: this.defaultPermissions,
                }),
                copied: false,

                updateApiTokenForm: this.$inertia.form({
                    permissions: []
                }),

                deleteApiTokenForm: this.$inertia.form(),

                displayingToken: false,
                managingPermissionsFor: null,
                apiTokenBeingDeleted: null,
            }
        },

    methods: {
            copiedToken() {
                this.copied = true
            },

            createApiToken() {
                this.createApiTokenForm.post(route('api-tokens.store'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.displayingToken = true
                        this.createApiTokenForm.reset()
                    }
                })
            },

            manageApiTokenPermissions(token) {
                this.updateApiTokenForm.permissions = token.abilities

                this.managingPermissionsFor = token
            },

            updateApiToken() {
                this.updateApiTokenForm.put(route('api-tokens.update', this.managingPermissionsFor), {
                    preserveScroll: true,
                    preserveState: true,
                    onSuccess: () => (this.managingPermissionsFor = null),
                })
            },

            confirmApiTokenDeletion(token) {
                this.apiTokenBeingDeleted = token
            },

            deleteApiToken() {
                this.deleteApiTokenForm.delete(route('api-tokens.destroy', this.apiTokenBeingDeleted), {
                    preserveScroll: true,
                    preserveState: true,
                    onSuccess: () => (this.apiTokenBeingDeleted = null),
                })
            },
        },
    }
</script>
